import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  Product,
  ProductsService,
} from '../../../services/products/products.service';
import {
  AssignmentView,
  StoreProductService,
} from '../../../services/store-product/store-product.service';
import {
  StoreOption,
  StoreService,
} from '../../../services/stores/store.service';

@Component({
  selector: 'app-assign-store-product',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './assign-store-product.component.html',
})
export class AssignStoreProductsComponent implements OnInit {
  private storeProductService = inject(StoreProductService);
  private storeService = inject(StoreService);
  private productsService = inject(ProductsService);
  private snackbar = inject(SnackbarService);

  stores = signal<StoreOption[]>([]);
  products = signal<Product[]>([]);
  assignments = signal<AssignmentView[]>([]);

  selectedStoreId: number | null = null;
  selectedProductId: number | null = null;
  selectedProduct = signal<Product | null>(null);
  querySearch = '';

  private assignmentVersion = 0;
  private loadedContext = '';
  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);

  ngOnInit() {
    this.loadStores();
  }

  loadStores() {
    this.storeService.allStores().subscribe({
      next: (data) => this.stores.set(data),
      error: () => this.snackbar.error('Failed to load stores'),
    });
  }

  onSearchProducts() {
    if (this.querySearch.length < 3) {
      this.products.set([]);
      return;
    }
    this.productsService
      .searchOrLatestProducts(this.querySearch, 10)
      .subscribe({
        next: (data) => this.products.set(data),
        error: () => this.snackbar.error('Failed to search products'),
      });
  }

  productImage(product: Product): string | null {
    return product.images?.find((image) => image.isPrimary && image.url)?.url
      || product.images?.find((image) => image.url)?.url
      || null;
  }

  hideBrokenImage(event: Event) {
    (event.target as HTMLImageElement).hidden = true;
  }

  selectProduct(product: Product) {
    this.selectedProduct.set(product);
    this.selectedProductId = product.id;
    this.loadAssignments();
  }

  loadAssignments() {
    const version = ++this.assignmentVersion;
    this.loadedContext = '';
    this.assignments.set([]);
    const storeId = this.selectedStoreId;
    const productId = this.selectedProductId;

    if (!storeId || !productId) { this.isLoading.set(false); return; }

    this.isLoading.set(true);
    this.storeProductService.getAssignments(storeId, productId).subscribe({
      next: (data) => {
        if (version !== this.assignmentVersion) return;
        this.loadedContext = `${storeId}:${productId}`;
        this.assignments.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        if (version !== this.assignmentVersion) return;
        this.snackbar.error('Failed to load assignments');
        this.isLoading.set(false);
      },
    });
  }

  toggleAll(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.assignments.update((items) =>
      items.map((item) => ({ ...item, isAssigned: checked }))
    );
  }

  applyPriceToAll() {
    const firstAssigned = this.assignments().find((a) => a.isAssigned);
    if (!firstAssigned) return;

    this.assignments.update((items) =>
      items.map((item) =>
        item.isAssigned ? { ...item, price: firstAssigned.price } : item
      )
    );
  }

  async saveAssignments() {
    const storeId = this.selectedStoreId;
    if (this.isSaving() || this.isLoading() || !storeId ||
        this.loadedContext !== `${storeId}:${this.selectedProductId}`) return;

    const payload = this.assignments()
      .filter((a) => a.isAssigned)
      .map((a) => ({
        store_id: storeId,
        variant_id: a.variant_id,
        price: Number(a.price),
        low_stock_threshold: Number(a.low_stock_threshold ?? 5),
        is_available: a.is_available,
      }));

    const removals = this.assignments().filter((item) =>
      !item.isAssigned && item.store_product_id !== undefined
    );
    if (!payload.length && !removals.length) {
      this.snackbar.error('No assignments to save or remove');
      return;
    }
    if (removals.some((item) => item.stock !== 0)) {
      this.snackbar.error('Stock remains for an unchecked variant. Reconcile stock to zero with a reason, or turn off Available instead.');
      return;
    }

    this.isSaving.set(true);
    try {
      for (const item of removals) {
        await firstValueFrom(this.storeProductService.deleteStoreProductById(item.store_product_id!, storeId));
      }
      if (payload.length) await this.storeProductService.bulkUpsertAssignments(payload);
      this.snackbar.success(payload.length ? 'Assignments saved.' : 'Product removed from this store.');
      this.loadAssignments();
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : 'Failed to save assignments';
      this.snackbar.error(msg);
      // Earlier removals may have succeeded before a later operation failed.
      this.loadAssignments();
    } finally {
      this.isSaving.set(false);
    }
  }
}
